import type { Tournament } from '../tournament/types.js';
import { allHistories } from '../tournament/views.js';
import { computeValue, directEncounter, type Descriptor } from './compute.js';
import { buildContext, type TiebreakContext } from './model.js';

export type { Descriptor } from './compute.js';
export { computeValue, directEncounter, tpr, ptp } from './compute.js';
export * from './model.js';

/**
 * Tiebreak names. FIDE C.07 (2026) codes as in MTB26 (the mandatory list
 * for endorsed programs), then US Chess Rule 34E tiebreaks.
 */
export const TIEBREAKS: Readonly<Record<string, { readonly name: string; readonly unit: Unit }>> = {
  PTS: { name: 'Points', unit: 'score' },
  BH: { name: 'Buchholz', unit: 'score' },
  FB: { name: 'Fore Buchholz', unit: 'score' },
  AOB: { name: "Average of opponents' Buchholz", unit: 'score2' },
  SB: { name: 'Sonneborn-Berger', unit: 'product' },
  DE: { name: 'Direct encounter', unit: 'score' },
  WIN: { name: 'Number of wins', unit: 'count' },
  WON: { name: 'Number of games won', unit: 'count' },
  BPG: { name: 'Games played with Black', unit: 'count' },
  BWG: { name: 'Games won with Black', unit: 'count' },
  REP: { name: 'Rounds elected to play', unit: 'count' },
  PS: { name: 'Progressive score', unit: 'score' },
  STD: { name: 'Standard points', unit: 'score' },
  KS: { name: 'Koya', unit: 'score' },
  ARO: { name: 'Average rating of opponents', unit: 'rating' },
  TPR: { name: 'Tournament performance rating', unit: 'rating' },
  PTP: { name: 'Perfect tournament performance', unit: 'rating' },
  APRO: { name: 'Average performance rating of opponents', unit: 'rating' },
  APPO: { name: 'Average perfect performance of opponents', unit: 'rating' },
  RTNG: { name: 'Rating', unit: 'rating' },
  TPN: { name: 'Tournament pairing number', unit: 'tpn' },
  MM: { name: 'Modified median (US Chess)', unit: 'score' },
  MED: { name: 'Median (US Chess)', unit: 'score' },
  SOLK: { name: 'Solkoff (US Chess)', unit: 'score' },
  CUM: { name: 'Cumulative (US Chess)', unit: 'score' },
  OPPCUM: { name: 'Cumulative of opposition (US Chess)', unit: 'score' },
  KASH: { name: 'Kashdan (US Chess)', unit: 'count' },
  BLACKS: { name: 'Most blacks (US Chess)', unit: 'count' },
  OPERF: { name: 'Opposition performance (US Chess)', unit: 'rating' },
};

type Unit = 'score' | 'score2' | 'product' | 'count' | 'rating' | 'tpn';

/** Common presets. C.07 itself has no default lists; these are practice. */
export const TIEBREAK_PRESETS: Readonly<Record<string, readonly string[]>> = {
  'FIDE Swiss': ['BH/C1', 'BH', 'SB', 'DE', 'WIN', 'BWG'],
  'FIDE Swiss (rated players)': ['BH/C1', 'BH', 'ARO/C1', 'DE', 'WIN'],
  'FIDE round robin': ['DE', 'WIN', 'SB', 'KS'],
  'US Chess Swiss (34E)': ['MM', 'SOLK', 'CUM', 'OPPCUM'],
  'US Chess round robin (34F)': ['SB', 'DE'],
};

export function tiebreakCodes(): string[] {
  return Object.keys(TIEBREAKS);
}

/**
 * Parse an MTB26 rank-order descriptor: NAME[/modifier][/option]...
 * Modifiers: Cn (cut n lowest), Mn (median: cut n lowest and highest),
 * L±n (Koya limit, in points). Options: P (forfeits as played), F (Fore),
 * R (reverse). The older "BH:C1" form is accepted too.
 */
export function parseDescriptor(text: string): Descriptor {
  const raw = text
    .trim()
    .toUpperCase()
    .replace(/:(?=[CML]\d|[CML][+-])/g, '/');
  const [nameRaw, ...mods] = raw.split('/');
  const name = (nameRaw ?? '').replace(/:(MP|GP)$/, '');
  if (TIEBREAKS[name] === undefined) {
    throw new Error(`unknown tiebreak "${text}" (known: ${tiebreakCodes().join(', ')})`);
  }
  let cutLow = 0;
  let cutHigh = 0;
  let limit = 0;
  let forfeitsPlayed = false;
  let fore = false;
  let reverse = false;
  for (const m of mods) {
    let x: RegExpExecArray | null;
    if ((x = /^C(\d+)$/.exec(m))) cutLow = Number(x[1]);
    else if ((x = /^M(\d+)$/.exec(m))) {
      cutLow = Number(x[1]);
      cutHigh = Number(x[1]);
    } else if ((x = /^L([+-]?\d+(?:\.\d)?)$/.exec(m))) limit = Math.round(Number(x[1]) * 10);
    else if (m === 'P') forfeitsPlayed = true;
    else if (m === 'F') fore = true;
    else if (m === 'R') reverse = true;
    else if (m === '') continue;
    else throw new Error(`unknown tiebreak modifier "${m}" in "${text}"`);
  }
  const parts = [name];
  if (cutLow > 0 && cutHigh === 0) parts.push(`C${cutLow}`);
  if (cutHigh > 0) parts.push(`M${cutHigh}`);
  if (limit !== 0) parts.push(`L${limit > 0 ? '+' : ''}${limit / 10}`);
  if (forfeitsPlayed) parts.push('P');
  if (fore) parts.push('F');
  if (reverse) parts.push('R');
  return { text: parts.join('/'), name, cutLow, cutHigh, limit, forfeitsPlayed, fore, reverse };
}

/** Format a tiebreak value for display. */
export function formatTiebreak(d: Descriptor, v: number): string {
  const unit = TIEBREAKS[d.name]?.unit ?? 'score';
  const sign = d.reverse ? -1 : 1;
  const x = v * sign;
  switch (unit) {
    case 'score':
      return Number.isInteger(x) ? (x / 10).toFixed(1) : (x / 10).toFixed(2);
    case 'score2':
      return (x / 10).toFixed(2);
    case 'product':
      return (x / 100).toFixed(2);
    case 'tpn':
      return String(-x);
    default:
      return String(Math.round(x));
  }
}

/** Tiebreak context for a tournament after `upToRound` rounds (default: all paired). */
export function tiebreakContext(t: Tournament, upToRound?: number): TiebreakContext {
  // By default, the rounds whose results are all in.
  let completed = 0;
  for (const r of t.rounds) {
    if (r.games.some((g) => g.result.white === '*' || g.result.black === '*')) break;
    completed++;
  }
  const rounds = Math.min(upToRound ?? completed, t.rounds.length);
  const histories = allHistories(t, rounds);
  const players = t.players.map((p) => ({
    id: p.pairingNumber,
    rating: p.fideRating ?? p.rating,
    history: histories.get(p.pairingNumber) ?? [],
  }));
  const system = t.settings.system;
  const predetermined =
    system === 'round-robin' || system === 'double-round-robin' || system === 'scheveningen';
  return buildContext(players, t.settings.pointSystem, rounds, t.settings.rounds, predetermined);
}

export interface RankedEntry {
  readonly id: number;
  /** 1-based rank; equal ranks are shared places. */
  readonly rank: number;
  readonly values: readonly number[];
}

/**
 * Rank participants by points, then each tiebreak in turn, applied to every
 * group still tied (C.07 4.2). Direct encounter is computed within the group
 * it is applied to. Participants equal on everything share the place.
 */
export function rankParticipants(
  ctx: TiebreakContext,
  descriptors: readonly Descriptor[],
): RankedEntry[] {
  const ids = [...ctx.entrants.keys()];
  const values = new Map<number, number[]>(ids.map((id) => [id, []]));
  const pts = parseDescriptor('PTS');
  let groups: number[][] = splitBy(ids, (id) => computeValue(ctx, id, pts));
  for (const d of descriptors) {
    const next: number[][] = [];
    for (const g of groups) {
      if (d.name === 'DE') {
        const de = g.length > 1 ? directEncounter(ctx, g, d) : new Map([[g[0] as number, 0]]);
        for (const id of g) (values.get(id) as number[]).push(de.get(id) ?? 0);
        // Direct encounter re-applies to the sub-groups it leaves tied (6.2).
        for (const sub of splitBy(g, (id) => de.get(id) ?? 0)) {
          if (sub.length > 1 && sub.length < g.length) {
            const again = directEncounter(ctx, sub, d);
            next.push(...splitBy(sub, (id) => again.get(id) ?? 0));
          } else next.push(sub);
        }
      } else {
        const val = new Map(g.map((id) => [id, computeValue(ctx, id, d)]));
        for (const id of g) (values.get(id) as number[]).push(val.get(id) as number);
        next.push(...splitBy(g, (id) => val.get(id) as number));
      }
    }
    groups = next;
  }
  const out: RankedEntry[] = [];
  let rank = 1;
  for (const g of groups) {
    for (const id of [...g].sort((a, b) => a - b))
      out.push({ id, rank, values: values.get(id) as number[] });
    rank += g.length;
  }
  return out;
}

/** Split ids into groups of equal value, best (highest) first. */
function splitBy(ids: readonly number[], f: (id: number) => number): number[][] {
  const vals = new Map(ids.map((id) => [id, f(id)]));
  const sorted = [...ids].sort(
    (a, b) => (vals.get(b) as number) - (vals.get(a) as number) || a - b,
  );
  const groups: number[][] = [];
  for (const id of sorted) {
    const last = groups[groups.length - 1];
    if (
      last !== undefined &&
      Math.abs((vals.get(last[0] as number) as number) - (vals.get(id) as number)) < 1e-9
    ) {
      last.push(id);
    } else groups.push([id]);
  }
  return groups;
}
